import React, { useRef, useState } from 'react';

import { LikeOutlined } from '@ant-design/icons';

import { POST_TYPE } from 'constants/global';
import { IReaction, REACTION_TYPE } from 'constants/types';
import {
  useAddReactionMutation,
  useDeleteReactionMutation,
  useGetUserReactionQuery,
} from 'services/reactionManagement';

import ReactionIcon from '../ReactionIcon';
import {
  ButtonContainer,
  ButtonIcon,
  ButtonText,
  Container,
  ReactionIconsContainer,
  StyledReactionIcon,
} from './styles';

interface LikeButtonProps {
  postId: string;
  postType: POST_TYPE;
  refetchReactions: () => void;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  postId,
  postType,
  refetchReactions,
}) => {
  const [hovering, setHovering] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const [addReaction] = useAddReactionMutation();
  const [deleteReaction] = useDeleteReactionMutation();
  const { data: userReaction, refetch } = useGetUserReactionQuery(postId);

  const handleDeleteReaction = async () => {
    try {
      await deleteReaction(postId);
      await refetch();
      await refetchReactions();
    } catch (error) {
      console.error('Error deleting reaction:', error);
    }
  };

  const handleReactionClick = async (type: REACTION_TYPE) => {
    const newReaction: IReaction = {
      type,
      postId,
      postType,
    };
    await addReaction(newReaction);
    await refetch();
    await refetchReactions();
    setHovering(false);
  };

  const handleMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setHovering(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setHovering(false);
    }, 900);
  };

  return (
    <Container onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <ButtonContainer
        onClick={userReaction ? handleDeleteReaction : undefined}
      >
        <ButtonIcon>
          {userReaction ? (
            <ReactionIcon reactionType={userReaction.type} size={26} />
          ) : (
            <LikeOutlined />
          )}
        </ButtonIcon>

        <ButtonText>{userReaction?.type ? 'Reacted' : 'Like'}</ButtonText>
      </ButtonContainer>
      {hovering && (
        <ReactionIconsContainer>
          {[
            REACTION_TYPE.LIKE,
            REACTION_TYPE.HEART,
            REACTION_TYPE.LIGHT_BULB,
          ].map((reactionType) => (
            <StyledReactionIcon
              key={reactionType}
              onClick={() => handleReactionClick(reactionType)}
            >
              <ReactionIcon reactionType={reactionType} />
            </StyledReactionIcon>
          ))}
        </ReactionIconsContainer>
      )}
    </Container>
  );
};

export default LikeButton;
